import React, { useCallback, MouseEvent } from 'react';
import { X, FileCode, FileText, Workflow, Loader2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useEditorTabs, EditorTab } from '@/contexts/EditorTabsContext';

// ============================================================================
// Content Type Icons
// ============================================================================

function getContentTypeIcon(contentType: string) {
  switch (contentType) {
    case 'agent':
      return <Workflow className="h-3.5 w-3.5" />;
    case 'prompt':
      return <FileText className="h-3.5 w-3.5" />;
    case 'skill':
      return <FileCode className="h-3.5 w-3.5" />;
    default:
      return <FileCode className="h-3.5 w-3.5" />;
  }
}

// ============================================================================
// Tab Component
// ============================================================================

interface TabItemProps {
  tab: EditorTab;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
  onCloseOthers: () => void;
  onCloseAll: () => void;
  onRevert: () => void;
}

function TabItem({
  tab,
  isActive,
  onSelect,
  onClose,
  onCloseOthers,
  onCloseAll,
  onRevert,
}: TabItemProps) {
  const [contextMenuOpen, setContextMenuOpen] = React.useState(false);

  // Handle middle-click to close
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button === 1) {
        // Middle click
        e.preventDefault();
        onClose();
      }
    },
    [onClose]
  );

  // Handle click to select
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (e.button === 0) {
        // Left click
        onSelect();
      }
    },
    [onSelect]
  );

  // Handle close button click
  const handleCloseClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  // Prevent DropdownMenuTrigger from opening on left-click
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only prevent default for left-click (button 0)
      // This stops DropdownMenuTrigger from opening the menu
      if (e.button === 0) {
        e.preventDefault();
      }
    },
    []
  );

  return (
    <DropdownMenu open={contextMenuOpen} onOpenChange={setContextMenuOpen}>
      <DropdownMenuTrigger asChild>
        <div
          className={cn(
            'group relative flex items-center gap-1.5 px-3 py-1.5 text-sm cursor-pointer select-none',
            'border-r border-border',
            'transition-colors duration-100',
            isActive
              ? 'bg-background text-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onPointerDown={handlePointerDown}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenuOpen(true);
          }}
        >
          {/* Loading or content type icon */}
          {tab.isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : (
            <span className="text-muted-foreground">
              {getContentTypeIcon(tab.contentType)}
            </span>
          )}

          {/* Read-only indicator */}
          {tab.isReadOnly && (
            <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          )}

          {/* Dirty indicator + Name */}
          <span className="flex items-center gap-1 max-w-[120px]">
            {tab.isDirty && (
              <span className="text-primary font-bold" title="Unsaved changes">
                •
              </span>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate">{tab.name}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {tab.name}
                {tab.isDirty && ' (unsaved)'}
                {tab.isReadOnly && ' (read-only)'}
              </TooltipContent>
            </Tooltip>
          </span>

          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-4 w-4 p-0 ml-1',
              'opacity-0 group-hover:opacity-100',
              isActive && 'opacity-60',
              'hover:opacity-100 hover:bg-muted-foreground/20 rounded-sm'
            )}
            onClick={handleCloseClick}
          >
            <X className="h-3 w-3" />
          </Button>

          {/* Active indicator bar */}
          {isActive && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuItem onClick={onClose}>
          Close
          <span className="ml-auto text-xs text-muted-foreground">Ctrl+W</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onCloseOthers}>Close Others</DropdownMenuItem>
        <DropdownMenuItem onClick={onCloseAll}>Close All</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onRevert} disabled={!tab.isDirty}>
          Revert Changes
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================================================
// TabBar Component
// ============================================================================

interface TabBarProps {
  className?: string;
}

export function TabBar({ className }: TabBarProps) {
  const {
    tabs,
    activeTabId,
    setActiveTab,
    closeTab,
    closeOtherTabs,
    closeAllTabs,
    revertTab,
  } = useEditorTabs();

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex items-center overflow-x-auto border-b border-border bg-muted/30',
        'scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent',
        className
      )}
    >
      {tabs.map((tab) => (
        <TabItem
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          onSelect={() => setActiveTab(tab.id)}
          onClose={() => closeTab(tab.id)}
          onCloseOthers={() => closeOtherTabs(tab.id)}
          onCloseAll={closeAllTabs}
          onRevert={() => revertTab(tab.id)}
        />
      ))}
    </div>
  );
}

export default TabBar;
